import { toBlob } from "html-to-image";
import html2canvas from "html2canvas";
import { getBasicDeviceInfo } from "Utils";

class FontManager {
  constructor() {
    this.fontCache = new Map();
    this.fontsLoaded = false;
    this.fontLoadPromise = null;
    this.loadedFonts = [];
    this.isSafari = this.detectSafari();
  }

  /**
   * Detect if the current browser is Safari
   * @returns {boolean} True if Safari, false otherwise
   */
  detectSafari() {
    const deviceInfo = getBasicDeviceInfo();
    const browserName = deviceInfo?.browser?.name?.toLowerCase();
    return browserName === "safari";
  }

  static FONT_DEFINITIONS = [
    {
      family: "KaTeX_Main",
      weight: "normal",
      style: "normal",
      urls: [
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Main-Regular.woff2",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Main-Regular.woff",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Main-Regular.ttf",
      ],
    },
    {
      family: "KaTeX_Math",
      weight: "normal",
      style: "italic",
      urls: [
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Math-Italic.woff2",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Math-Italic.woff",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Math-Italic.ttf",
      ],
    },
    {
      family: "KaTeX_Size1",
      weight: "normal",
      style: "normal",
      urls: [
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size1-Regular.woff2",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size1-Regular.woff",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size1-Regular.ttf",
      ],
    },
    {
      family: "KaTeX_Size2",
      weight: "normal",
      style: "normal",
      urls: [
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size2-Regular.woff2",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size2-Regular.woff",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size2-Regular.ttf",
      ],
    },
    {
      family: "KaTeX_Size3",
      weight: "normal",
      style: "normal",
      urls: [
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size3-Regular.woff2",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size3-Regular.woff",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size3-Regular.ttf",
      ],
    },
    {
      family: "KaTeX_Size4",
      weight: "normal",
      style: "normal",
      urls: [
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size4-Regular.woff2",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size4-Regular.woff",
        "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Size4-Regular.ttf",
      ],
    },
  ];

  /**
   * Convert font file to base64 data URL with caching
   * @param {string} url - Font file URL
   * @returns {Promise<string|null>} Base64 data URL or null if failed
   */
  async fetchFontAsDataURL(url) {
    if (this.fontCache.has(url)) {
      return this.fontCache.get(url);
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
      const mimeType = this.getMimeTypeFromUrl(url);
      const dataURL = `data:${mimeType};base64,${base64}`;

      this.fontCache.set(url, dataURL);
      return dataURL;
    } catch (error) {
      console.warn(`Failed to fetch font from ${url}:`, error.message);
      return null;
    }
  }

  /**
   * Get MIME type based on file extension
   * @param {string} url - Font file URL
   * @returns {string} MIME type
   */
  getMimeTypeFromUrl(url) {
    if (url.endsWith(".woff2")) return "font/woff2";
    if (url.endsWith(".woff")) return "font/woff";
    if (url.endsWith(".ttf")) return "font/ttf";
    return "font/woff2"; // Default fallback
  }

  /**
   * Get CSS font format string from URL
   * @param {string} url - Font file URL
   * @returns {string} CSS format string
   */
  getFormatFromUrl(url) {
    if (url.endsWith(".woff2")) return "woff2";
    if (url.endsWith(".woff")) return "woff";
    if (url.endsWith(".ttf")) return "truetype";
    return "woff2";
  }

  /**
   * Load a single font definition with URL fallbacks
   * @param {Object} fontDef - Font definition object
   * @returns {Promise<Object|null>} Loaded font object or null
   */
  async loadFont(fontDef) {
    for (const url of fontDef.urls) {
      const dataURL = await this.fetchFontAsDataURL(url);
      if (dataURL) {
        return { ...fontDef, dataURL, sourceUrl: url };
      }
    }

    console.error(`Failed to load font: ${fontDef.family}`);
    return null;
  }

  /**
   * Preload all required fonts
   * @returns {Promise<Array>} Array of successfully loaded fonts
   */
  async preloadFonts() {
    if (this.fontsLoaded) {
      return this.loadedFonts;
    }

    if (this.fontLoadPromise) {
      return this.fontLoadPromise;
    }

    this.fontLoadPromise = Promise.all(
      FontManager.FONT_DEFINITIONS.map(fontDef => this.loadFont(fontDef))
    ).then(results => {
      this.loadedFonts = results.filter(Boolean);
      this.fontsLoaded = true;

      return this.loadedFonts;
    });

    return this.fontLoadPromise;
  }

  /**
   * Generate CSS with embedded font data
   * @returns {string} CSS string with @font-face rules
   */
  generateFontEmbedCSS() {
    if (!this.fontsLoaded || !this.loadedFonts.length) {
      console.warn("Fonts not loaded, using fallback CSS");
      return this.getFallbackCSS();
    }

    return this.loadedFonts
      .map(
        font =>
          `@font-face {
        font-family: "${font.family}";
        font-style: ${font.style};
        font-weight: ${font.weight};
        src: url("${font.dataURL}") format("${this.getFormatFromUrl(
            font.sourceUrl
          )}");
        font-display: block;
      }`
      )
      .join("\n\n");
  }

  /**
   * Fallback CSS for when font loading fails
   * @returns {string} Basic CSS with CDN font references
   */
  getFallbackCSS() {
    return `@font-face {
  font-family: "KaTeX_Main";
  src: url("https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Main-Regular.woff2") format("woff2");
  font-display: block;
}
@font-face {
  font-family: "KaTeX_Math"; 
  font-style: italic;
  src: url("https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts/KaTeX_Math-Italic.woff2") format("woff2");
  font-display: block;
}`;
  }

  /**
   * Convert DOM element to PNG using html2canvas (Safari)
   * @param {HTMLElement} element - Element to convert
   * @param {Object} options - Conversion options
   * @returns {Promise<Blob>} PNG blob
   */
  async convertWithHtml2Canvas(element, options = {}) {
    const config = {
      scale: options.pixelRatio || 5,
      logging: false,
      ...options,
    };

    const canvas = await html2canvas(element, config);
    return new Promise(resolve => {
      canvas.toBlob(resolve, "image/png");
    });
  }

  /**
   * Convert DOM element to PNG using html-to-image (non-Safari)
   * @param {HTMLElement} element - Element to convert
   * @param {Object} options - Conversion options
   * @returns {Promise<Blob>} PNG blob
   */
  async convertWithHtmlToImage(element, options = {}) {
    await this.preloadFonts();

    const config = {
      height: options.height,
      width: options.width,
      backgroundColor: null,
      pixelRatio: options.pixelRatio || 5,
      fontEmbedCSS: this.generateFontEmbedCSS(),
      ...options,
    };

    try {
      return await toBlob(element, config);
    } catch (error) {
      console.error("html-to-image conversion failed:", error.message);

      // Retry with fallback CSS
      const fallbackConfig = { ...config, fontEmbedCSS: this.getFallbackCSS() };
      return await toBlob(element, fallbackConfig);
    }
  }

  /**
   * Convert DOM element to PNG with browser-specific method
   * @param {HTMLElement} element - Element to convert
   * @param {Object} options - Conversion options
   * @returns {Promise<Blob>} PNG blob
   */
  async convertToBlob(element, options = {}) {
    if (this.isSafari) {
      return this.convertWithHtml2Canvas(element, options);
    } else {
      return this.convertWithHtmlToImage(element, options);
    }
  }
}

// Singleton instance
const fontManager = new FontManager();

// Public API
export const preloadMathFonts = () => fontManager.preloadFonts();
export const convertMathToBlob = (element, options) =>
  fontManager.convertToBlob(element, options);

export default fontManager;
